import { supabase } from '../supabase';

const ALERT_CANDIDATE_SELECT = `
  *,
  candidate:candidates(full_name, email, linkedin_url, avatar, current_position, current_company, zoho_id)
`;

const getTodayDateRange = () => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  return { startOfToday, endOfToday };
};

/**
 * Get all alerts for a user
 */
export const getAlerts = async (userId, filters = {}) => {
  let query = supabase
    .from('alerts')
    .select(ALERT_CANDIDATE_SELECT)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  // Filter by status
  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  // Filter by alert type
  if (filters.alertType) {
    query = query.eq('alert_type', filters.alertType);
  }

  // Filter by date range
  if (filters.startDate) {
    query = query.gte('created_at', filters.startDate);
  }
  if (filters.endDate) {
    query = query.lte('created_at', filters.endDate);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
};

export const dedupeAlertsByCandidate = (alerts, limit) => {
  const seen = new Set();
  const unique = [];

  for (const alert of alerts || []) {
    const key = alert.candidate_id || `alert:${alert.id}`;
    if (seen.has(key)) continue;

    seen.add(key);
    unique.push(alert);

    if (unique.length >= limit) break;
  }

  return unique;
};

/**
 * Latest alerts across all time, one per candidate (most recent alert wins).
 */
export const getRecentUniqueAlerts = async (userId, limit = 10) => {
  const fetchLimit = Math.max(limit * 10, 50);

  const { data, error } = await supabase
    .from('alerts')
    .select(ALERT_CANDIDATE_SELECT)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(fetchLimit);

  if (error) throw error;
  return dedupeAlertsByCandidate(data, limit);
};

/**
 * Get alerts created today (local timezone)
 */
export const getTodayAlerts = async (userId, limit = 20) => {
  const { startOfToday, endOfToday } = getTodayDateRange();

  const { data, error } = await supabase
    .from('alerts')
    .select(ALERT_CANDIDATE_SELECT)
    .eq('user_id', userId)
    .gte('created_at', startOfToday.toISOString())
    .lte('created_at', endOfToday.toISOString())
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
};

/**
 * Count alerts created today (local timezone)
 */
export const getTodayAlertsCount = async (userId) => {
  const { startOfToday, endOfToday } = getTodayDateRange();

  const { count, error } = await supabase
    .from('alerts')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', startOfToday.toISOString())
    .lte('created_at', endOfToday.toISOString());

  if (error) throw error;
  return count || 0;
};

/**
 * Get recent alerts (last 7 days)
 */
export const getRecentAlerts = async (userId, limit = 20) => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const { data, error } = await supabase
    .from('alerts')
    .select(ALERT_CANDIDATE_SELECT)
    .eq('user_id', userId)
    .gte('created_at', sevenDaysAgo.toISOString())
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
};

/**
 * Get pending alerts count
 */
export const getPendingAlertsCount = async (userId) => {
  const { count, error } = await supabase
    .from('alerts')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('status', 'pending');

  if (error) throw error;
  return count;
};

/**
 * Get alerts count by type for last 7 days
 * Filters by user_id and updated_at within the last 7 days
 */
export const getAlertsStats = async (userId) => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const { data, error } = await supabase
    .from('alerts')
    .select('alert_type')
    .eq('user_id', userId)
    .gte('updated_at', sevenDaysAgo.toISOString());

  if (error) throw error;

  const stats = {
    total: data.length,
    new_post: data.filter(a => a.alert_type === 'new_post').length,
    new_certification: data.filter(a => a.alert_type === 'new_certification').length,
    open_to_work: data.filter(a => a.alert_type === 'open_to_work').length,
  };

  return stats;
};

/**
 * Create alert
 */
export const createAlert = async (userId, alertData) => {
  const { data, error } = await supabase
    .from('alerts')
    .insert({
      user_id: userId,
      status: 'pending',
      metadata: {},
      ...alertData,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Mark alert as resolved
 */
export const resolveAlert = async (alertId, actionTaken = '') => {
  const { data, error } = await supabase
    .from('alerts')
    .update({
      status: 'resolved',
      action_taken: actionTaken,
      resolved_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', alertId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete alert
 */
export const deleteAlert = async (alertId) => {
  const { error } = await supabase
    .from('alerts')
    .delete()
    .eq('id', alertId);

  if (error) throw error;
};

/**
 * Bulk resolve alerts
 */
export const bulkResolveAlerts = async (alertIds, actionTaken = '') => {
  const { data, error } = await supabase
    .from('alerts')
    .update({
      status: 'resolved',
      action_taken: actionTaken,
      resolved_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .in('id', alertIds)
    .select();

  if (error) throw error;
  return data;
};

/**
 * Get alerts for a specific candidate
 */
export const getCandidateAlerts = async (candidateId) => {
  const { data, error } = await supabase
    .from('alerts')
    .select('*')
    .eq('candidate_id', candidateId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};
